import { AxiosResponse } from 'axios';
import { getReportFileName } from '@app/dashboard/utils/getReportFileName';
import { REPORTS_STATEMENTS_EVENTS_VALUES } from '@app/dashboard/constants/tracking_events/reports-statements';

const getHeaderString = (
  headers: AxiosResponse['headers'],
  key: string
): string | undefined => {
  const value = headers[key];
  return typeof value === 'string' ? value : undefined;
};

const getExtensionFromFilename = (filename: string): string | undefined => {
  if (!filename.includes('.')) {
    return undefined;
  }
  return filename.split('.').pop()?.toLowerCase();
};

const resolveFormat = (extension?: string): string => {
  if (extension === 'xlsx') {
    return REPORTS_STATEMENTS_EVENTS_VALUES.FILE_FORMAT.XLSX;
  }
  if (extension === 'pdf') {
    return REPORTS_STATEMENTS_EVENTS_VALUES.FILE_FORMAT.PDF;
  }
  if (extension === 'csv') {
    return REPORTS_STATEMENTS_EVENTS_VALUES.FILE_FORMAT.CSV;
  }
  return extension || 'unknown';
};

const triggerBrowserDownload = (blob: Blob, downloadName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', downloadName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export type TDownloadReportFileResult = {
  downloadName: string;
  extension?: string;
  format: string;
};

export const downloadReportFile = ({
  res,
}: {
  res: AxiosResponse<Blob>;
}): TDownloadReportFileResult | null => {
  const contentDisposition = getHeaderString(res.headers, 'content-disposition');
  const filename = getReportFileName(contentDisposition);

  if (!filename) {
    return null;
  }

  const contentTypeHeader = getHeaderString(res.headers, 'content-type');
  const contentType =
    contentTypeHeader?.split(';')[0] ||
    res.data?.type ||
    'application/octet-stream';

  const extension = getExtensionFromFilename(filename);

  triggerBrowserDownload(new Blob([res.data], { type: contentType }), filename);

  return {
    downloadName: filename,
    extension,
    format: resolveFormat(extension),
  };
};
